import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CHOICE_STAGES,
  REPLACEMENT_STAGES,
  isCheckComplete,
  useDeclineVehicle,
  useRejectSlot,
  type RejectReason,
  type SelectionSlot,
  type SelectionState,
} from '@entities/selection';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { Button, ButtonLink } from '@shared/ui';

import { DeclineDialog } from './DeclineDialog';
import { RejectDialog } from './RejectDialog';
import styles from './SelectionActions.module.css';

interface SelectionActionsProps {
  searchId: string;
  slot: SelectionSlot;
  selection: SelectionState;
  /** `page` — на странице автомобиля кнопки стоят в строку, а не столбцом. */
  layout?: 'card' | 'page' | undefined;
}

/**
 * Действия по автомобилю четвёрки (§34.4–34.5).
 *
 * Один компонент на оба экрана — карточку четвёрки и страницу автомобиля
 * внутри заказа. Две копии этой логики разошлись бы на первой же правке, а
 * расхождение здесь означает, что на одном экране можно то, чего нельзя на
 * другом, — при том что действие одно и то же.
 *
 * **Действий три, и они не взаимозаменяемы** (решение заказчика от 09.09.2026):
 *
 * * **«Не подходит»** — до подтверждения четвёрки. Расходует одну из восьми
 *   замен и **приводит следующего кандидата** рабочего набора;
 * * **«Выбрать этот автомобиль»** — как только Karl закончил вопросы по нему
 *   (`check_completed_at`). Ведёт на экран мандата: границы торга задаются
 *   вместе с выбором, одним действием и одним запросом (§21). Выбор **один на
 *   заказ и окончателен**: остальные три выбывают, вернуть их можно только
 *   через поддержку — снятия с торга нет;
 * * **«Отказаться от этого автомобиля»** — после подтверждения четвёрки.
 *   Замены не будет, слот остаётся пустым, четвёрка становится тройкой (§34.5).
 *
 * **Пустого места на месте кнопки не бывает.** Пока проверка идёт, вместо
 * кнопки выбора стоит строка «Karl уточняет сведения» — причина, а не
 * неактивная кнопка без объяснения (FE-400).
 *
 * **Красных кнопок на карточке нет** (DESIGN_SYSTEM §5.1): четыре одинаковые
 * красные на четвёрке — это ноль главных действий. На карточке «Выбрать» —
 * синий контур `outline-action`, «Не подходит» и «Отказаться» — `ghost` с
 * подтверждением во втором шаге. На странице автомобиля (`layout="page"`)
 * автомобиль один, и прежние варианты там сохранены: тот экран эта правка не
 * перекраивает.
 *
 * Необратимые действия — в два шага и без оптимистичного UI (CLAUDE.md §8).
 */
export function SelectionActions({
  searchId,
  slot,
  selection,
  layout = 'card',
}: SelectionActionsProps) {
  const { t } = useTranslation();
  const rejectSlot = useRejectSlot(searchId);
  const declineVehicle = useDeclineVehicle(searchId);

  const [rejecting, setRejecting] = useState(false);
  const [declining, setDeclining] = useState(false);

  const selected = slot.status === 'selected';
  const chosenElsewhere = !selected && selection.selected_vehicle_id !== null;
  const replaceable =
    !selection.frozen && (REPLACEMENT_STAGES as readonly string[]).includes(selection.stage);
  const choiceStage = (CHOICE_STAGES as readonly string[]).includes(selection.stage);
  // Только по завершённой проверке **этого** автомобиля (12.09.2026): стадия
  // заказа здесь не заменяет `check_completed_at` — она говорит про четвёрку
  // целиком, а выбирают один автомобиль.
  const checkDone = isCheckComplete(slot);
  const selectable = !selected && !chosenElsewhere && choiceStage && checkDone;
  const checking = !selected && !chosenElsewhere && choiceStage && !checkDone;
  const busy = rejectSlot.isPending || declineVehicle.isPending;

  const onCard = layout === 'card';
  const selectVariant = onCard ? 'outline-action' : 'primary';
  const removeVariant = onCard ? 'ghost' : 'secondary';

  return (
    <>
      <div className={onCard ? styles.column : styles.row}>
        {selectable ? (
          // «Выбрать» ведёт на карточку переписки (FE-460): опросник перед
          // торгом — условия, максимум, вопросы — живёт там, и выбор с границами
          // торга уходит одним действием (§21). Без переписки (контур был
          // выключен) — прежний экран мандата с тем же опросником.
          <ButtonLink
            to={
              slot.negotiation_id === null
                ? ROUTES.searchMandate(searchId, slot.vehicle.vehicle_id)
                : ROUTES.negotiation(slot.negotiation_id)
            }
            variant={selectVariant}
            fullWidth={onCard}
            aria-disabled={busy}
          >
            {t('selection.card.select')}
          </ButtonLink>
        ) : null}

        {replaceable ? (
          // «Не подходит» — третичное действие: основное действие на карточке
          // одно (DESIGN_SYSTEM §5.1). Подтверждение — во втором шаге.
          <Button
            variant={removeVariant}
            size={onCard ? 'sm' : 'md'}
            fullWidth={onCard}
            disabled={busy}
            onClick={() => {
              setRejecting(true);
            }}
          >
            {t('selection.card.reject')}
          </Button>
        ) : null}

        {selection.frozen && choiceStage && !selected && !chosenElsewhere ? (
          <Button
            variant={removeVariant}
            size={onCard ? 'sm' : 'md'}
            fullWidth={onCard}
            disabled={busy}
            onClick={() => {
              setDeclining(true);
            }}
          >
            {t('selection.card.decline')}
          </Button>
        ) : null}
      </div>

      {/* Проверка идёт: Karl сам дописывает автохаусу, пока по каждому
          пункту нет ответа или отказа (§36.6). Кнопки выбора нет, и сказано
          почему. */}
      {checking ? (
        <p className={styles.frozen} role="status">
          {t('selection.card.checkPending')}
        </p>
      ) : null}

      {/* Замен больше нет — и сказано **почему**, а не показана неактивная
          кнопка: пользователь должен понимать, что изменилось и когда. */}
      {selectable ? (
        <p className={styles.frozen} role="note">
          {t('selection.card.frozenNotice')}
        </p>
      ) : null}

      {/* Выбор сделан и окончателен: снятия с торга нет, и это сказано прямо
          (§34.4). */}
      {selected ? (
        <p className={styles.frozen} role="note">
          {t('selection.card.selectedFinal')}
        </p>
      ) : null}

      <RejectDialog
        slot={rejecting ? slot : null}
        bargaining={selected}
        busy={rejectSlot.isPending}
        error={errorText(rejectSlot.error, t)}
        onClose={() => {
          rejectSlot.reset();
          setRejecting(false);
        }}
        onConfirm={(reason: RejectReason | undefined) => {
          rejectSlot.mutate(
            { slot: slot.slot, vehicleId: slot.vehicle.vehicle_id, reason },
            {
              onSuccess: () => {
                setRejecting(false);
              },
            },
          );
        }}
      />

      <DeclineDialog
        slot={declining ? slot : null}
        bargaining={selected}
        busy={declineVehicle.isPending}
        error={errorText(declineVehicle.error, t)}
        onClose={() => {
          declineVehicle.reset();
          setDeclining(false);
        }}
        onConfirm={() => {
          declineVehicle.mutate(slot.vehicle.vehicle_id, {
            onSuccess: () => {
              setDeclining(false);
            },
          });
        }}
      />
    </>
  );
}

/**
 * Текст отказа по коду ответа.
 *
 * Код без перевода не показывается: `SELECTION_FROZEN` на экране — это утечка
 * внутренностей, поэтому есть общий запасной текст.
 */
function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `selection.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('selection.error.text') : translated;
}
